import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { Link as RouterLink } from 'react-router-dom';
import { CardRowSkeleton, StatCardsSkeleton } from '../components/UI/PageSkeleton';
import { SubscriptionHistoryItem, WalletSummary } from '../types';
import { fetchDriverWallet } from '../services/driverWorkspaceService';
import { cancelSubscription, fetchSubscriptionHistory, resumeSubscription } from '../services/subscriptionService';
import { fetchPaymentGateways, GatewayId, initiateWithFallback, openCashfreeCheckout, verifyPayment } from '../services/paymentService';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

declare global {
  interface Window {
    Razorpay?: any;
  }
}

const Wallet: React.FC = () => {
  const { user } = useAuth();
  const isVerified = user?.emailVerified !== false;
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [gateways, setGateways] = useState<Array<{ id: GatewayId; mode: 'test' | 'live' }>>([]);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState('');
  const [couponError, setCouponError] = useState('');
  const [planHistory, setPlanHistory] = useState<SubscriptionHistoryItem[]>([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [planActionBusy, setPlanActionBusy] = useState(false);
  const [tab, setTab] = useState(0);

  const loadWallet = async () => {
    const response = await fetchDriverWallet();
    setWallet(response);
  };

  const loadPlanHistory = () => fetchSubscriptionHistory().then(setPlanHistory).catch(() => {});

  useEffect(() => {
    loadWallet().finally(() => setLoading(false));
    loadPlanHistory();
    fetchPaymentGateways()
      .then((response) => setGateways(response.gateways || []))
      .catch(() => setGateways([]));
  }, []);

  const handleCancelPlan = async () => {
    setPlanActionBusy(true);
    try {
      await cancelSubscription();
      setCancelDialogOpen(false);
      setMessage('Cancellation scheduled — your plan stays active until the period ends.');
      await Promise.all([loadWallet(), loadPlanHistory()]);
    } catch (err: any) {
      setMessage(err?.response?.data?.message || 'Unable to cancel plan.');
    } finally {
      setPlanActionBusy(false);
    }
  };

  const handleKeepPlan = async () => {
    setPlanActionBusy(true);
    try {
      await resumeSubscription();
      setMessage('Your plan will continue as normal.');
      await Promise.all([loadWallet(), loadPlanHistory()]);
    } catch (err: any) {
      setMessage(err?.response?.data?.message || 'Unable to resume plan.');
    } finally {
      setPlanActionBusy(false);
    }
  };

  const loadRazorpay = () => new Promise<boolean>((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      await api.get('/api/public/coupons/validate', { params: { code: couponCode.trim().toUpperCase() } });
      setCouponApplied(couponCode.trim().toUpperCase());
      setCouponError('');
      setMessage(`Coupon "${couponCode.trim().toUpperCase()}" applied!`);
    } catch (err: any) {
      setCouponError(err?.response?.data?.message || 'Invalid or expired coupon code.');
      setCouponApplied('');
    }
  };

  const buyTopUp = async (packId: string) => {
    const preferredGateway = gateways[0]?.id;
    if (!preferredGateway) {
      setMessage('No payment gateway is enabled.');
      return;
    }

    setWorkingId(packId);
    try {
      const { data: init } = await initiateWithFallback(
        { orderType: 'top_up', topUpPackId: packId, ...(couponApplied ? { couponCode: couponApplied } : {}) },
        preferredGateway,
        gateways.map((gateway) => gateway.id)
      );

      if (init.gateway === 'razorpay') {
        const loaded = await loadRazorpay();
        if (!loaded) {
          setMessage('Unable to load Razorpay checkout.');
          return;
        }

        const razorpay = new window.Razorpay({
          key: init.keyId,
          amount: init.amount,
          currency: init.currency,
          name: 'RideSaathi',
          description: init.description,
          order_id: init.orderId,
          handler: async (response: any) => {
            await verifyPayment({
              gateway: 'razorpay',
              paymentOrderId: init.paymentOrderId,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            await loadWallet();
            setMessage('Extra unlocks added.');
          },
        });
        razorpay.open();
        return;
      }

      if (init.gateway === 'paytm') {
        localStorage.setItem('paymentReturn', JSON.stringify({ gateway: 'paytm', paymentOrderId: init.paymentOrderId }));
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${init.paytmHost}/theia/api/v1/showPaymentPage?mid=${init.mid}&orderId=${init.orderId}`;
        const txnTokenInput = document.createElement('input');
        txnTokenInput.type = 'hidden';
        txnTokenInput.name = 'txnToken';
        txnTokenInput.value = init.txnToken;
        form.appendChild(txnTokenInput);
        document.body.appendChild(form);
        form.submit();
        return;
      }

      if (init.gateway === 'phonepe') {
        localStorage.setItem('paymentReturn', JSON.stringify({ gateway: 'phonepe', paymentOrderId: init.paymentOrderId }));
        window.location.href = init.redirectUrl;
        return;
      }

      if (init.gateway === 'cashfree') {
        await openCashfreeCheckout(init);
        return;
      }

      setMessage('Unsupported payment gateway.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Unable to start payment.');
    } finally {
      setWorkingId(null);
    }
  };

  const downloadInvoice = async (paymentOrderId: string) => {
    try {
      const response = await api.get(`/api/invoices/${paymentOrderId}/download`, { responseType: 'blob' });
      // Server sends a PDF (falls back to HTML only if rendering is unavailable)
      const contentType = response.headers['content-type'] || 'application/pdf';
      const isPdf = contentType.includes('pdf');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${paymentOrderId}.${isPdf ? 'pdf' : 'html'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setMessage('Unable to download invoice.');
    }
  };

  if (loading) {
    return (
      <Stack spacing={3} sx={{ maxWidth: 1180, mx: 'auto' }}>
        <CardRowSkeleton cols={2} />
        <StatCardsSkeleton count={3} />
        <CardRowSkeleton cols={2} />
      </Stack>
    );
  }

  if (!wallet) {
    return (
      <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
        <AccountBalanceWalletIcon sx={{ fontSize: 52, color: 'rgba(7,24,45,0.18)', mb: 1.5 }} />
        <Typography variant="h6" fontWeight={700} mb={0.75}>Wallet unavailable</Typography>
        <Typography color="text.secondary" mb={2.5}>We couldn't load your wallet data. Please try refreshing.</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>Reload</Button>
      </Paper>
    );
  }

  const quotaPercent = wallet.entitlements.monthlyLeadLimit
    ? Math.min((wallet.entitlements.used / wallet.entitlements.monthlyLeadLimit) * 100, 100)
    : 0;
  const totalCredits = wallet.entitlements.remaining;
  const planRemaining = wallet.entitlements.planRemaining ?? (
    wallet.entitlements.monthlyLeadLimit === null ? null : Math.max((wallet.entitlements.monthlyLeadLimit || 0) - wallet.entitlements.used, 0)
  );
  const walletCredits = wallet.entitlements.walletCredits || 0;
  const creditLabel = totalCredits === null ? 'Unlimited unlocks' : `${totalCredits} credits available`;

  return (
    <Stack spacing={3} sx={{ maxWidth: 1180, mx: 'auto' }}>
      {wallet.lowCredits && (
        <Alert severity="warning" action={<Button component={RouterLink} to="/plans" color="inherit" size="small">Upgrade</Button>}>
          You are low on unlock credits. Add a top-up pack or upgrade before fresh rides arrive.
        </Alert>
      )}

      <Paper variant="outlined">
        <Tabs
          value={tab}
          onChange={(_e, value) => setTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ px: 1, '& .MuiTab-root': { fontWeight: 800 } }}
        >
          <Tab label="Overview" />
          <Tab label="History" />
          <Tab label="Coupons" />
        </Tabs>
      </Paper>

      {tab === 0 && (<>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
              <Box>
                <Typography variant="h5" fontWeight={900}>Available credits</Typography>
                <Typography variant="body2" color="text.secondary">
                  Plan: {wallet.entitlements.planName}
                </Typography>
              </Box>
              <Chip
                label={creditLabel}
                color={wallet.lowCredits ? 'warning' : 'success'}
              />
            </Box>
            <Box mt={3}>
              {wallet.entitlements.monthlyLeadLimit === null ? (
                <Typography color="text.secondary">Your current plan has unlimited ride unlocks.</Typography>
              ) : wallet.entitlements.monthlyLeadLimit === 0 ? (
                <Stack spacing={1}>
                  <Typography color="text.secondary">
                    Your free plan uses wallet credits instead of a monthly unlock quota.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined" color={walletCredits > 0 ? 'success' : 'default'} label={`Wallet: ${walletCredits}`} />
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  <LinearProgress variant="determinate" value={quotaPercent} sx={{ height: 10, borderRadius: 5 }} />
                  <Typography variant="caption" color="text.secondary">
                    {wallet.entitlements.used} used from {wallet.entitlements.monthlyLeadLimit} monthly plan unlocks.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined" label={`Plan left: ${planRemaining ?? 0}`} />
                    <Chip size="small" variant="outlined" color={walletCredits > 0 ? 'success' : 'default'} label={`Wallet: ${walletCredits}`} />
                  </Stack>
                </Stack>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Typography variant="h6" fontWeight={900}>Current plan</Typography>
              {wallet.subscription?.cancelAtPeriodEnd && (
                <Chip
                  size="small"
                  color="warning"
                  label={`Cancels on ${new Date(wallet.subscription.endsAt).toLocaleDateString()}`}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {wallet.subscription
                ? wallet.subscription.cancelAtPeriodEnd
                  ? `${wallet.subscription.plan?.name || 'Plan'} — access until ${new Date(wallet.subscription.endsAt).toLocaleDateString()}, then you move to Free.`
                  : `${wallet.subscription.plan?.name || 'Plan'} — active until ${new Date(wallet.subscription.endsAt).toLocaleDateString()}.`
                : 'Free plan. Upgrade when you need more rides.'}
            </Typography>
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
              <Button component={RouterLink} to="/plans" variant="contained" color="secondary">Choose / upgrade plan</Button>
              <Button component={RouterLink} to="/activity" variant="outlined">History</Button>
              {wallet.subscription && !wallet.subscription.cancelAtPeriodEnd && (
                <Button color="error" variant="text" disabled={planActionBusy} onClick={() => setCancelDialogOpen(true)}>
                  Cancel plan
                </Button>
              )}
              {wallet.subscription?.cancelAtPeriodEnd && (
                <Button color="success" variant="outlined" disabled={planActionBusy} onClick={handleKeepPlan}>
                  Keep plan
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={900}>Top-up packs</Typography>
            <Typography variant="body2" color="text.secondary">Buy extra unlocks without changing your plan.</Typography>
          </Box>
          {gateways.length === 0 && <Chip label="Payments unavailable" color="warning" variant="outlined" />}
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={2} alignItems="flex-start">
          <TextField
            size="small"
            label="Coupon code"
            value={couponCode}
            onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
            placeholder="Enter code"
            error={Boolean(couponError)}
            helperText={couponError || (couponApplied ? `✓ "${couponApplied}" applied` : '')}
            sx={{ minWidth: 200 }}
          />
          <Button variant="outlined" size="small" onClick={applyCoupon} sx={{ mt: { xs: 0, sm: 0.5 } }}>
            Apply
          </Button>
          {couponApplied && (
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={() => { setCouponApplied(''); setCouponCode(''); }}
              sx={{ mt: { xs: 0, sm: 0.5 } }}
            >
              Remove
            </Button>
          )}
        </Stack>
        {wallet.topUpPacks.length === 0 ? (
          <Alert severity="info">Top-up packs are not available right now.</Alert>
        ) : (
          <Grid container spacing={2}>
            {wallet.topUpPacks.map((pack) => (
              <Grid key={pack.id} item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: '#fbfcff' }}>
                  <Stack spacing={1.25}>
                    <Typography fontWeight={900}>{pack.label}</Typography>
                    <Typography variant="h4" fontWeight={900}>{pack.currency === 'INR' ? 'Rs ' : ''}{pack.price}</Typography>
                    <Typography variant="body2" color="text.secondary">{pack.credits} ride unlocks added after payment.</Typography>
                    <Button
                      variant="contained"
                      disabled={!wallet.entitlements.topUpAllowed || workingId === pack.id || !isVerified}
                      onClick={() => buyTopUp(pack.id)}
                    >
                      {isVerified ? 'Add ride credits' : 'Verify email to recharge'}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
      </>)}

      {tab === 1 && (<>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={900}>Payment history</Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.5}>
              {wallet.paymentOrders.length === 0 && <Typography color="text.secondary">No payment attempts yet.</Typography>}
              {wallet.paymentOrders.map((order) => (
                <Box key={order.id} display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography fontWeight={800}>{order.itemName}</Typography>
                      <Chip
                        label={order.status || 'paid'}
                        size="small"
                        color={order.status === 'failed'
                          ? 'error'
                          : order.status === 'created'
                            ? 'warning'
                            : order.status === 'refunded'
                              ? 'info'
                              : 'success'}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {order.gateway} · {new Date(order.paidAt).toLocaleDateString()} · {order.currency} {order.amount}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    {(order.status || 'paid') === 'paid' && (
                      <>
                        <Button size="small" variant="outlined" component={RouterLink} to={`/invoice/${order.id}`}>
                          View
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => downloadInvoice(order.id)}>
                          Download
                        </Button>
                      </>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h6" fontWeight={900}>Credit ledger</Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.25}>
              {wallet.creditTransactions.length === 0 && <Typography color="text.secondary">No credit adjustments yet.</Typography>}
              {wallet.creditTransactions.map((transaction) => (
                <Box key={transaction.id} display="flex" justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="body2" fontWeight={800}>{transaction.reason || transaction.type.replace(/_/g, ' ')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(transaction.createdAt).toLocaleDateString()}
                      {transaction.balanceAfter !== undefined && transaction.balanceAfter !== null
                        ? ` · Balance ${transaction.balanceAfter}`
                        : ''}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${transaction.amount > 0 ? '+' : ''}${transaction.amount}`}
                    size="small"
                    color={transaction.amount > 0 ? 'success' : transaction.amount < 0 ? 'error' : 'default'}
                    variant={transaction.amount < 0 ? 'outlined' : 'filled'}
                  />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      </>)}

      {tab === 2 && (
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="h6" fontWeight={900}>Available coupons</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Coupons are applied during payment checkout when eligible.
            </Typography>
            <Stack spacing={1.25} mt={2}>
              {(wallet.availableCoupons || []).length === 0 && (
                <Typography color="text.secondary">No active coupons right now.</Typography>
              )}
              {(wallet.availableCoupons || []).map((coupon) => (
                <Paper key={coupon.id} variant="outlined" sx={{ p: 1.5, bgcolor: '#fbfcff' }}>
                  <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                    <Box>
                      <Typography fontWeight={900}>{coupon.code}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {coupon.description || (coupon.discountType === 'percent'
                          ? `${coupon.discountValue}% off eligible payments`
                          : `Rs ${coupon.discountValue} off eligible payments`)}
                      </Typography>
                    </Box>
                    {coupon.validUntil && (
                      <Chip label={`Until ${new Date(coupon.validUntil).toLocaleDateString()}`} size="small" variant="outlined" />
                    )}
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="h6" fontWeight={900}>Coupon history</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Discounts used on successful payments.
            </Typography>
            <Stack spacing={1.25} mt={2}>
              {(wallet.couponHistory || []).length === 0 && (
                <Typography color="text.secondary">No coupon usage yet.</Typography>
              )}
              {(wallet.couponHistory || []).map((coupon) => (
                <Box key={coupon.id} display="flex" justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="body2" fontWeight={900}>{coupon.code}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {coupon.orderName} · {new Date(coupon.usedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Chip label={`Saved Rs ${coupon.discountAmount}`} size="small" color="success" variant="outlined" />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      )}

      {tab === 1 && (
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="h6" fontWeight={900}>Plan history</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Every plan you have held, with dates and what you paid.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={1.25}>
          {planHistory.length === 0 && <Typography color="text.secondary">No plan history yet.</Typography>}
          {planHistory.map((item) => (
            <Box key={item.id} display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
              <Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography fontWeight={800}>{item.planName}</Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={item.status === 'cancels_at_period_end' ? `cancels ${item.endsAt ? new Date(item.endsAt).toLocaleDateString() : ''}` : item.status}
                    color={item.status === 'active'
                      ? 'success'
                      : item.status === 'cancels_at_period_end'
                        ? 'warning'
                        : item.status === 'canceled'
                          ? 'error'
                          : 'default'}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {new Date(item.startsAt).toLocaleDateString()}
                  {item.endsAt ? ` → ${new Date(item.endsAt).toLocaleDateString()}` : ''}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={800} color={item.granted ? 'text.secondary' : 'text.primary'}>
                {item.granted
                  ? 'Granted'
                  : item.priceAtPurchase !== null
                    ? `₹${item.priceAtPurchase}`
                    : '—'}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
      )}

      {/* Cancel plan confirmation */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Cancel your plan?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              You keep <b>{wallet.subscription?.plan?.name}</b> access until{' '}
              <b>{wallet.subscription ? new Date(wallet.subscription.endsAt).toLocaleDateString() : ''}</b>. After that you
              move to the Free plan.
            </Typography>
            <Alert severity="info">
              Unused days are not refunded on cancellation — if you want their value, switch plans instead and they
              become wallet credits.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep my plan</Button>
          <Button color="error" variant="contained" disabled={planActionBusy} onClick={handleCancelPlan}>
            {planActionBusy ? 'Cancelling…' : 'Cancel plan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(message)} autoHideDuration={3500} onClose={() => setMessage(null)} message={message} />
    </Stack>
  );
};

export default Wallet;
